<template>
    <div class="app-toast__wrapper">
        <breaking-changes-warning v-if="hasDataObjectBreakingChanges(allDataObjects)" :toggle="true" @toggle="toggleBreakingChanges = $event" />
    </div>
    <div class="app-project-dashboard">
        <div class="page-main__section-header">
            <h2 class="page-main__section-title">
                Learning Objects
            </h2>
            <slot name="navigation"></slot>
        </div>
        <div class="app-project-dashboard__filters grid">
            <div class="col-12 col-sm-6 col-md-3">
                <form @submit.prevent="search"
                    class="app-filters__search app-search">
                    <div class="input text">
                        <input
                            type="search"
                            placeholder="Search for keywords or phrases"
                            v-model="input"
                            @search="onSearch"
                            required>
                        <!-- <button type="submit" class="btn btn-icon btn-outline-secondary" :disabled="loading"><i class="icon-search"></i></button> -->
                    </div>
                </form>
            </div>
            <div class="col-12 col-sm-6 col-md-3">
                <div class="app-project-dashboard__filters-types input select">
                    <select v-model="type">
                        <option :value="null" selected>All screen types</option>
                        <optgroup v-for="(group, key) in screenTypes"
                            :key="`category-group-${key}`"
                            :label="key.toUpperCase()">
                            <option v-for="(type, index) in group"
                                :key="'type-' + index"
                                :value="type">
                                {{ type.toUpperCase() }}
                            </option>
                        </optgroup>
                    </select>
                </div>
            </div>
            <div class="col-12 col-sm-6 col-md-3">
                <div class="app-project-dashboard__filters-layouts input select">
                    <select v-model="layout">
                        <option :value="null" selected>All screen layouts</option>
                        <optgroup v-for="(group, key) in screenLayouts"
                            :key="`type-group-${key}`"
                            :label="key.toUpperCase()">
                            <option v-for="(screen, index) in group"
                                :key="'layout-' + index"
                                :value="screen.layout">
                                {{ screen.name }}
                            </option>
                        </optgroup>
                    </select>
                </div>
            </div>
            <div class="col-12 col-sm-6 col-md-3">
                <div class="app-project-dashboard__filters-layouts input select">
                    <select id="course-filter" v-model="selectedCourse" @change="fetchLearningObjects">
                        <option :value="null" selected>All Publishing Courses</option>
                        <option v-for="pub in publications" :key="pub.id" :value="pub.id">
                            {{ pub.name }}
                        </option>
                    </select>
                </div>
            </div>
            <div class="col-12 col-sm-6 col-md-4 col-lg-3">
                <div class="app-project-dashboard__filters-buttons element-button-group">
                    <button class="element-button-group__button" @click="collapseAll()">Collapse All</button>
                    <button class="element-button-group__button" @click="expandAll()">Expand All</button>
                </div>
            </div>
            <div class="app-project-dashboard__filters-sort-parent col-12 col-md-8 col-lg-9">
                <div class="app-project-dashboard__filters-sort">
                    <slot name="sort-learning-button"></slot>
                    <div class="input select">
                        <select v-model="sortBy">
                            <option :value="null" selected>Sort by...</option>
                            <option v-for="(type, index) in sortByTypes"
                                :key="'type-' + index"
                                :value="type.value">
                                Sort by {{ type.label }}
                            </option>
                        </select>
                    </div>
                </div>
            </div>
            <div class="col-12">
                <div class="app-project-dashboard__filters-messaging">
                    <div v-show="loading" class="app-loader__wrapper">
                        <app-loader />
                    </div>
                    <p class="inline-message is--error" v-if="error">{{ error }}</p>
                    <p class="inline-message" v-else-if="status">{{ status }}</p>
                    <p class="app-project-dashboard__results">Screen results: {{ totalCount }}</p>
                </div>
            </div>
        </div>
        <div class="col-lg-12">
            <app-paginator :items="filteredResults" :items-per-page="itemsPerPage">
                <template v-slot:item="paging">
                    <div :class="['app-project-dashboard__lo', 'app-accordion', isOpen(paging.item.id) ? 'is--open' : '']" :key="`learning-${paging.item.id}`">
                        <div :class="['app-project-dashboard__lo-header', 'app-accordion__header', { 'is--disabled': !paging.item.screens.length }]" @click="toggle(paging.item.id, $event)">
                            <svg class="app-project-dashboard__lo-header-expand element-icon--chevron-down element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-chevron-down"></use></svg>
                            <h3 class="app-project-dashboard__lo-header-title app-accordion__title">
                                <span class="app-project-dashboard__lo-id" v-if="paging.item.internal_id">{{ paging.item.internal_id }}</span>
                                {{ paging.item.name }}
                            </h3>
                            <a @click="$event.isAction = true" class="element-button--secondary element-button" :href="`/learning/${paging.item.id}/edit`">Edit</a>
                            <!-- <button type="button" class="element-button--primary element-button">Publish</button> -->
                            <div @click="$event.isAction = true" class="dropdown">
                                <button class="element-table__actions-button element-button--icon element-button" type="button" data-bs-toggle="dropdown">
                                    <svg class="element-icon--more-info element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-more-info"></use></svg>
                                </button>
                                <div class="app-dropdown dropdown-menu dropdown-menu-right">
                                    <a :href="paging.item.preview_url" target="_blank">
                                        <svg aria-hidden="true" class="element-icon--preview element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-preview"></use></svg>
                                        Preview
                                    </a>
                                    <a :href="`/learning/${paging.item.id}/download`" v-if="paging.item.is_published">
                                        <svg aria-hidden="true" class="element-icon--download element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-download"></use></svg>
                                        Download
                                    </a>
                                    <button v-if="!hasBasicAccess" type="button" @click="duplicateLearning(paging.item)">
                                        <svg aria-hidden="true" class="element-icon--copy element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-copy"></use></svg>
                                        Duplicate
                                    </button>
                                    <button v-if="!hasBasicAccess" class="is--alert" type="button" @click="deleteLearning(paging.item)">
                                        <svg aria-hidden="true" class="element-icon--delete element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-delete"></use></svg>
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div v-show="isOpen(paging.item.id)" class="app-project-dashboard__lo-body app-accordion__body">
                            <div class="element-table__wrapper">
                                <table v-show="paging.item.screens.length">
                                    <thead class="app-project-dashboard__lo-body-header">
                                        <tr>
                                            <th>Screen Title</th>
                                            <th width="100px">Screen ID</th>
                                            <th width="200px">Screen Type</th>
                                            <th width="57px"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr :key="`screen-${screen.id}`"
                                            v-for="screen in paging.item.screens"
                                            class="app-project-dashboard__lo-screen">
                                            <td>
                                                <span class="app-project-dashboard__lo-body-title">
                                                    <span v-if="majorChanges(screen)" class="app-project-dashboard__lo-body-title-breaking-change">
                                                        <span data-bs-toggle="dropdown" class="element-featured-icon is--error is--xs">
                                                            <svg class="element-icon--error element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-error"></use></svg>
                                                        </span>
                                                        <div class="app-tooltip--process-images app-tooltip dropdown-menu">
                                                            <h4 class="app-tooltip__title">This Screen Needs Edits</h4>
                                                            <div class="app-tooltip__body">
                                                                <p>A recent release caused a breaking change in this screen that requires you to make changes to how it works.</p>
                                                            </div>
                                                        </div>
                                                    </span>
                                                    <span v-else-if="minorChanges(screen)" class="app-project-dashboard__lo-body-title-breaking-change">
                                                        <span data-bs-toggle="dropdown" class="element-featured-icon is--warning is--xs">
                                                            <svg class="element-icon--warning element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-warning"></use></svg>
                                                        </span>
                                                        <div class="app-tooltip--process-images app-tooltip dropdown-menu">
                                                            <h4 class="app-tooltip__title">This Screen Needs to Be Resaved</h4>
                                                            <div class="app-tooltip__body">
                                                                <p>A recent release caused a minor breaking change in this screen that can be resolved by resaving the screen.</p>
                                                            </div>
                                                        </div>
                                                    </span>
                                                    {{ screen.title }}
                                                </span>
                                            </td>
                                            <td class="app-project-dashboard__lo-body-id">{{ screen.name }}</td>
                                            <td class="app-project-dashboard__lo-body-type"><p class="element-badge">{{ getInteractionName(screen) }}</p></td>
                                            <td><a :href="`/learning/${paging.item.id}/edit#${screen.id}`" class="element-button--secondary element-button">Edit</a></td>
                                        </tr>
                                    </tbody>
                                </table>
                                <div v-show="!paging.item.data_objects.length" class="empty-table">No screens have been added. Select "Edit" to get started!</div>
                            </div>
                        </div>
                    </div>
                </template>
            </app-paginator>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import { getInteraction, getInteractionId } from '../helpers/builder'
import BreakingChangesWarning from './BreakingChangesWarning.vue';

export default {
    components: {
        BreakingChangesWarning
    },
    props: {
        project: {
            type: Object
        },
        schemaVersions: {
            type: [Array, Object, null],
        }
    },
    data () {
        return {
            source: this.project,
            learningObjects: this.project.learning_objects,
            input: '',
            query: '',
            type: null,
            layout: null,
            expanded: [],
            loading: false,
            status: false,
            error: false,
            schemaVersionAlert: null,
            schemaVersionsOutdated: [],
            itemsPerPage: 20,
            publications: this.project.publications || [],
            publicationObjectFilter: null,
            selectedCourse: null,
            matches: [],
            sortBy: null,
            allDataObjects: [],
            toggleBreakingChanges: false,
            sortByTypes: [
                { label: 'Alphabetical', value: 'name_with_internal_id' },
                { label: 'Created date', value: 'created_at' },
                { label: 'Last modified date', value: 'updated_at' },
                { label: 'Custom order', value: 'sort_index' }
            ]
        }
    },
    computed: {
        ...mapGetters([
            'hasBasicAccess'
        ]),
        ...mapGetters('builder', [
            'hasDataObjectBreakingChanges',
            'majorChanges',
            'minorChanges',
        ]),
        filteredResults () {
            let filtered = [];
            if (this.toggleBreakingChanges) {
                filtered = _.reduce(this.learningObjects, (result, object) => {
                    const screens = _.filter(object.data_objects, this.hasMatchBreakingChanges)
                    if (screens.length && (!this.hasAnyQuery || this.hasTitleMatch(object))) {
                        result.push({ ...object, screens })
                    }
                    return result
                }, [])
            } else {
                filtered = _.reduce(this.learningObjects, (result, object) => {
                    const screens = _.filter(object.data_objects, this.hasMatch)
                    if (screens.length || !this.hasAnyQuery || this.hasTitleMatch(object)) {
    
                        // defaults to true, unless publication filter is set
                        if (this.hasPublicationMatch(object))
                            result.push({ ...object, screens })
                    }
                    return result
                }, [])
            }
            return _.sortBy(filtered, this.sortBy);
        },
        totalCount () {
            return _.sumBy(this.filteredResults, ({ screens }) => screens.length)
        },
        interactions () {
            return _.get(this.source, 'driver.interactions', [])
        },
        screenLayouts () {
            return _.reduce(this.interactions, (result, { type, layout, name }) => {
                // not matching the filter, skip
                if (this.type && type !== this.type) {
                    return result
                }

                // ensure array exists
                if (!result[type]) {
                    result[type] = []
                }

                // only add unique values
                const value = layout || type
                if (result[type].indexOf(value) === -1) {
                    result[type].push({ type, layout: value, name })
                }
                return result
            }, { })
        },
        screenTypes () {
            return _.reduce(this.interactions, (result, { category, type }) => {
                if (!result[category]) {
                    result[category] = []
                }
                if (result[category].indexOf(type) === -1) {
                    result[category].push(type)
                }
                return result
            }, { })
        },
        hasAnyQuery () {
            return this.query || this.type || this.layout
        }
    },
    watch: {
        type (value) {
            const selectedLayout = _.find(this.interactions, ({ type, layout }) => {
                return this.layout === layout || this.layout === type
            })

            if (selectedLayout && selectedLayout.type !== value) {
                this.layout = null
            }

            // if there is only one layout, auto-select
            // if (this.screenTypes[this.type].length === 1) {
            //     this.layout = this.screenLayouts[this.type][0]
            // }
        }
    },
    methods: {
        getInteraction: getInteraction,
        getInteractionName (screen) {
            const interaction = this.getInteraction(screen)
            const object = _.find(this.interactions, interaction)
            return object ? object.name : screen.type
        },
        toggle (id, $event) {
            if ($event.isAction) {
                return
            }
            const index = this.expanded.indexOf(id)
            if (index >= 0) {
                this.expanded.splice(index, 1)
            } else {
                this.expanded.push(id)
            }
        },
        isOpen (id) {
            return this.expanded.indexOf(id) >= 0
        },
        expandAll () {
            this.expanded = _.map(this.learningObjects, 'id')
        },
        collapseAll () {
            this.expanded = []
        },
        hasMatch (screen) {
            // console.log(screen)
            const interaction = this.getInteraction(screen)

            const typeMatch = !this.type || interaction.type === this.type
            const layoutMatch = !this.layout || interaction.layout === this.layout

            const queryMatch = !this.query || this.matches.indexOf(screen.id) >= 0

            return queryMatch && typeMatch && layoutMatch
        },
        hasMatchBreakingChanges (screen) {
            return this.hasDataObjectBreakingChanges([screen])
        },
        hasTitleMatch ({ name_with_internal_id }) {
            return this.query && name_with_internal_id.toLowerCase().indexOf(this.query.toLowerCase()) >= 0
        },
        hasPublicationMatch ({ id }) {
            if (!this.publicationObjectFilter)
                return true

            if (this.publicationObjectFilter)
                return this.publicationObjectFilter.some((object) => object.id === id)
            else 
                return false
        },
        onSearch () {
            this.query = this.input
            this.matches = []
        },
        async search () {
            this.setStatus(`Searching for: "${this.input}"`)

            this.query = this.input
            try {
                const response = await axios.get(`/projects/${this.project.id}/search`, { params: { query: this.query } })
                this.matches = _.get(response, 'data.data.results')
                this.expandAll()
            } catch (err) {
                this.displayError(err, `Error getting search results for: "${this.query}"`)
            }
            
            // this.clearStatus()
            if (this.filteredResults.length) {
                this.setStatus(`Showing results for: "${this.query}"`, false)
            } else {
                this.setStatus(`No results.`, false)
            }
        },
        async duplicateLearning (learningObject) {
            this.setStatus(`Duplicating: ${learningObject.name}...`)

            try {
                const response = await axios.post(`/learning/${learningObject.id}/duplicate`)
                const result = _.get(response, 'data.data')

                if (result) {
                    this.learningObjects.push(result)
                } else {
                    console.error(response)
                }
            } catch (err) {
                this.displayError(err, `Error duplicating learning: "${learningObject.name}"`)
            }
            
            this.clearStatus()
        },
        async deleteLearning ({ id }) {
            if (!confirm('Are you sure you want to delete this Learning Object?')) {
                return
            }

            this.setStatus('Deleting learning...')

            const index = _.findIndex(this.learningObjects, { id })
            const removedObject = this.learningObjects.splice(index, 1)

            try {
                const response = await axios.delete(`/learning/${id}`)
                const result = _.get(response, 'data.status')

                if (result !== 'success') {
                    this.learningObjects.splice(index, 0, removedObject)
                }
            } catch (err) {
                this.displayError(err, `Error deleting learning: "${removedObject.name}"`)
                this.learningObjects.splice(index, 0, removedObject)
            }

            this.clearStatus()
        },
        async loadContent () {
            this.setStatus('Loading content...')

            try {
                const response = await axios.get(`/projects/${this.project.id}`)
                const content = _.get(response, 'data.data')
                if (content) {
                    this.source = content
                    this.learningObjects = this.source.learning_objects
                    this.allDataObjects = _.flatMap(this.learningObjects, 'data_objects')
                }
            } catch (err) {
                this.displayError(err, 'Unable to load content.')
            }

            this.clearStatus()
        },
        setStatus (status, loading = true) {
            this.loading = loading
            this.status = status
        },
        clearStatus (loading = false) {
            this.status = false
            this.loading = loading
        },
        displayError (err, genericMessage) {
            console.error(err)
            const errors = _.get(err, 'response.data.errors', false)
            this.error = errors[0] || genericMessage
        },
        async fetchLearningObjects() {
            if (this.selectedCourse) {
                this.setStatus('Loading learning objects...');
                try {
                    const response = await axios.get(`/projects/${this.project.id}/publication-objects`, {
                        params: { publication_id: this.selectedCourse }
                    });
                    const content = _.get(response, 'data.data')
                    if (content)
                        this.publicationObjectFilter = content
                } catch (error) {
                    this.displayError(error, 'Error fetching learning objects.')
                } finally {
                    this.clearStatus()
                }
            }
            else
                this.publicationObjectFilter = null
        }
    },
    mounted () {
        this.loadContent()
        console.log('test',this)
        this.$store.commit('builder/setSchemaVersions', this.schemaVersions)
    }
}
</script>
